// Money — every currency amount in the admin renders through here.
//
// 🔴 This component exists because `lib/billing.ts` shipped a formatter that
// divided KWD/BHD/OMR by 1000 and then formatted to TWO decimals, rendering
// 1.234 KWD as "1.23". Amounts must never be formatted ad hoc in a component
// again; `lib/money.ts` owns the arithmetic and this owns the presentation.
//
// Always tabular-nums so columns of figures align on the decimal point.

import { cn } from '@fajrarabi/ui';

import { formatMinorAmount } from '~/lib/money';

export interface MoneyProps {
  amountMinor: number;
  currency: string;
  /** Show the currency code. Off inside a column already headed by currency. */
  showCurrency?: boolean;
  /** Colour negatives red and prefix `−`. For refund/credit lines. */
  signed?: boolean;
  className?: string;
  /** `strong` for totals and detail pages; `muted` for secondary figures. */
  emphasis?: 'normal' | 'strong' | 'muted';
}

export function Money({
  amountMinor,
  currency,
  showCurrency = true,
  signed = false,
  className,
  emphasis = 'normal',
}: MoneyProps) {
  const negative = amountMinor < 0;
  const formatted = formatMinorAmount(Math.abs(amountMinor), currency);
  const code = currency.toUpperCase();

  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        emphasis === 'strong' && 'font-semibold text-admin-900',
        emphasis === 'muted' && 'text-admin-500',
        signed && negative && 'text-error-600',
        className,
      )}
      // The accessible name spells the amount out; a screen reader announcing
      // "QAR 12.34" is far clearer than the visual grouping characters.
      title={`${code} ${formatted}`}
    >
      {signed && negative && '−'}
      {showCurrency && <span className="text-admin-400">{code} </span>}
      {formatted}
    </span>
  );
}

/**
 * A currency total that refuses to blend currencies. Renders one line per
 * currency — with seven currencies and no FX table, a single summed figure
 * would be fiction (see `sumMinor` in lib/money.ts).
 */
export function MoneyTotals({
  totals,
  className,
  emphasis = 'strong',
}: {
  totals: ReadonlyArray<{ currency: string; amountMinor: number }>;
  className?: string;
  emphasis?: MoneyProps['emphasis'];
}) {
  if (totals.length === 0) {
    return <span className={cn('text-admin-400', className)}>—</span>;
  }
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {totals.map((t) => (
        <Money
          key={t.currency}
          amountMinor={t.amountMinor}
          currency={t.currency}
          emphasis={emphasis}
        />
      ))}
    </div>
  );
}
